import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useHubAction } from '@/hooks/use-hub-action';
import { api, type FleetPromptRow, type RepoRow } from '@/lib/api';

/**
 * Standing instructions, edited: one form, used at both levels.
 *
 * What it is for is the thing a job cannot say. A job carries its goal, its
 * constraints and its acceptance criteria, all written for that job; there was
 * nowhere to put what is true of every job in a repo - which channel finished
 * work is announced in, which conventions its pull requests follow, what an
 * agent must never touch. People wrote those into every brief by hand, or into
 * the repo's own CLAUDE.md, which puts an operations decision inside a file the
 * agent is also editing.
 *
 * Two fields, because they reach the agent two different ways and neither
 * substitutes for the other. Instructions arrive as a section of the task
 * prompt, beside the spec, which is where behaviour belongs. The system prompt
 * is appended to the agent's own, framing the session rather than sitting in one
 * message, which is where a role or a standing constraint belongs. The help text
 * under each says exactly that, because an admin choosing between two big
 * textareas with no explanation will pick by which one is nearer.
 *
 * Empty means empty. There is no "unset" a person can express that differs from
 * clearing the box, so the form posts both fields every time and the hub trims
 * blank to null.
 *
 * Both editors seed their fields from props once and then own them. The caller
 * keys them on what it read, so a saved row remounts with the saved text and a
 * person typing is never overwritten mid-sentence by a refetch - which is what
 * an effect syncing props into state would have done.
 */

const INSTRUCTIONS_HELP =
  'Read by every agent run, as a section beside the task spec. Behaviour goes here: where to announce finished work, which conventions to follow, what never to touch.';
const SYSTEM_PROMPT_HELP =
  'Appended to the agent’s own system prompt, so it frames the whole session rather than one message. A role or a standing constraint goes here.';

function PromptFields({
  idPrefix,
  instructions,
  systemPrompt,
  onInstructions,
  onSystemPrompt,
  disabled,
}: {
  idPrefix: string;
  instructions: string;
  systemPrompt: string;
  onInstructions: (value: string) => void;
  onSystemPrompt: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-instructions`}>Instructions</Label>
        <Textarea
          id={`${idPrefix}-instructions`}
          rows={8}
          maxLength={20_000}
          value={instructions}
          disabled={disabled}
          onChange={(event) => onInstructions(event.target.value)}
          placeholder={'When a pull request is ready, post it in #team-ai-engineering-agents…'}
        />
        <p className="text-fg-3 text-2xs">{INSTRUCTIONS_HELP}</p>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-system`}>System prompt</Label>
        <Textarea
          id={`${idPrefix}-system`}
          rows={5}
          maxLength={20_000}
          value={systemPrompt}
          disabled={disabled}
          onChange={(event) => onSystemPrompt(event.target.value)}
          placeholder="You are working in an agent-operated repository…"
        />
        <p className="text-fg-3 text-2xs">{SYSTEM_PROMPT_HELP}</p>
      </div>
    </>
  );
}

/**
 * The fleet level: what every run this hub dispatches is told, whichever repo
 * it lands in. One row, so this is an upsert and there is nothing to add or
 * remove.
 */
export function FleetPromptEditor({ prompt }: { prompt: FleetPromptRow | null }) {
  const [instructions, setInstructions] = useState(prompt?.instructions ?? '');
  const [systemPrompt, setSystemPrompt] = useState(prompt?.systemPrompt ?? '');

  const save = useHubAction(
    async () => {
      await api.setFleetPrompt({ instructions, systemPrompt });
    },
    {
      success: 'Fleet instructions saved. They apply from the next task dispatched.',
      inlineError: true,
    },
  );

  const dirty =
    instructions !== (prompt?.instructions ?? '') || systemPrompt !== (prompt?.systemPrompt ?? '');

  return (
    <form
      className="space-y-3 px-gutter py-3"
      onSubmit={(event) => {
        event.preventDefault();
        save.run();
      }}
    >
      <PromptFields
        idPrefix="fleet"
        instructions={instructions}
        systemPrompt={systemPrompt}
        onInstructions={setInstructions}
        onSystemPrompt={setSystemPrompt}
        disabled={save.busy}
      />

      {save.error ? (
        <p className="text-bad text-xs" role="alert">
          {save.error}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={save.busy || !dirty}>
          {save.busy ? 'Saving…' : 'Save fleet instructions'}
        </Button>
        {prompt ? (
          <span className="text-fg-3 text-2xs">
            Last edited by {prompt.updatedBy}. A project can add to this; where they conflict, the
            project wins.
          </span>
        ) : (
          <span className="text-fg-3 text-2xs">
            Nothing set. Every run is told only what its own project says.
          </span>
        )}
      </div>
    </form>
  );
}

/**
 * The project level, under its row in the registry.
 *
 * The announce switch lives here rather than beside the text because it is a
 * different kind of decision: the text says what to post and where, and this
 * says whether to spend one more agent turn saying it. They are set together
 * and written together, and the hub records one audit line for the three.
 */
export function RepoPromptEditor({ repo, onClose }: { repo: RepoRow; onClose: () => void }) {
  const [instructions, setInstructions] = useState(repo.instructions ?? '');
  const [systemPrompt, setSystemPrompt] = useState(repo.systemPrompt ?? '');
  const [announceOnReady, setAnnounceOnReady] = useState(repo.announceOnReady);

  const save = useHubAction(
    async () => {
      await api.setRepoPrompt(repo.slug, { instructions, systemPrompt, announceOnReady });
    },
    {
      success: `${repo.slug} instructions saved. They apply from the next task dispatched.`,
      onDone: onClose,
      inlineError: true,
    },
  );

  return (
    <form
      className="bg-sunken space-y-3 px-gutter py-3"
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        save.run();
      }}
    >
      <p className="text-fg-2 text-xs">
        Read by every agent run in <span className="font-medium">{repo.slug}</span>, on top of the
        fleet instructions above. Where the two conflict, this wins.
      </p>

      <PromptFields
        idPrefix={`repo-${repo.slug}`}
        instructions={instructions}
        systemPrompt={systemPrompt}
        onInstructions={setInstructions}
        onSystemPrompt={setSystemPrompt}
        disabled={save.busy}
      />

      <label className="flex items-start gap-2 text-xs">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={announceOnReady}
          disabled={save.busy}
          onChange={(event) => setAnnounceOnReady(event.target.checked)}
        />
        <span>
          <span className="font-medium">Announce the pull request when it is ready</span>
          <span className="text-fg-3 block text-2xs">
            The agent stops speaking before the worker opens the pull request, so a run has no link
            to post at the end. This spends one more turn, after the flip to ready, where the link
            exists. It needs an MCP server the repo declares in its .dyspatch.json that can reach
            the channel your instructions name.
          </span>
        </span>
      </label>

      {save.error ? (
        <p className="text-bad text-xs" role="alert">
          {save.error}
        </p>
      ) : null}

      <div className="flex items-center gap-1.5">
        <Button type="submit" size="sm" disabled={save.busy}>
          {save.busy ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
